import { initContract } from '@ts-rest/core';
import {
  AutomationPreferenceSchema,
  UpdateAutomationRequestSchema,
  RecordConsentRequestSchema,
  EmailPreferencesSchema,
  UpdateEmailPreferenceRequestSchema,
} from '../schemas/preferences';
import { defaultErrorResponses } from './errors';

const c = initContract();

/**
 * Preferences Contract - automation mode + first-run consent (epic #139).
 *
 * All endpoints require auth. `updateAutomation` and `recordConsent` return 403
 * FORBIDDEN for non-Pro users. `getAutomation` is safe for all tiers and
 * returns `is_eligible=false` for Free users (used by the upsell UI).
 */
export const preferencesContract = c.router({
  getAutomation: {
    method: 'GET',
    path: '/v1/preferences/automation',
    responses: {
      200: AutomationPreferenceSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get automation preferences',
      description:
        'Returns current automation mode, first-run consent timestamp, Pro eligibility, and the per-track auto-apply policy summary.',
    },
  },
  updateAutomation: {
    method: 'PUT',
    path: '/v1/preferences/automation',
    body: UpdateAutomationRequestSchema,
    responses: {
      200: AutomationPreferenceSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Update automation mode (Pro only)',
      description:
        'Switches between auto_apply and classify_only. Returns 403 for Free-tier users.',
    },
  },
  recordConsent: {
    method: 'POST',
    path: '/v1/preferences/automation/consent',
    body: RecordConsentRequestSchema,
    responses: {
      200: AutomationPreferenceSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Record first-run Auto-Apply consent (Pro only)',
      description:
        'Idempotently records that the user has seen the onboarding consent screen and is enabling Auto-Apply. Sets automation_consent_at and automation_mode=auto_apply. Returns 403 for Free-tier users.',
    },
  },
  getEmailPreferences: {
    method: 'GET',
    path: '/v1/preferences/email',
    responses: {
      200: EmailPreferencesSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get optional email preferences',
      description:
        'Returns lifecycle and feedback email permissions, prompt history, and cooldown eligibility.',
    },
  },
  updateEmailPreference: {
    method: 'PUT',
    path: '/v1/preferences/email',
    body: UpdateEmailPreferenceRequestSchema,
    responses: {
      200: EmailPreferencesSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Update an optional email preference',
      description:
        'Records purpose-specific consent, prompt impressions, or prompt dismissals. Consent changes are mirrored to purpose-specific ESP groups.',
    },
  },
});
